import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  Bird,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Droplet,
  HeartPulse,
  Thermometer,
  Weight,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { ModalOverlay } from '@/components/ModalOverlay';
import { NaveEvolutionChart } from '@/components/NaveEvolutionChart';
import { NaveSensorAlerts, NaveSensors } from '@/components/NaveSensors';
import { fmt1, fmtFecha, fmtFechaHora, fmtInt } from '@/lib/format';
import { expectedWeight, sampleSummary, sensorAverage } from '@/lib/homeSummary';
import { simulationLabel } from '@/lib/iotPresentation';
import { historicalLosses } from '@/lib/naveHistory';
import { formatoDecimal } from '@/lib/liquidacion';
import { useEntityId } from '@/lib/offline/operations';
import { useCamada, useCamadaActiva } from '@/lib/queries/camadas';
import { useSensoresNave } from '@/lib/queries/iot';
import { applySensorEquipment, getSensorEquipment } from '@/lib/sensorEquipment';
import { useLotesDeCamada } from '@/lib/queries/lotesNave';
import { useNaves } from '@/lib/queries/naves';
import { useNaveHistoryBundle } from '@/lib/queries/naveHistory';
import { useCurvaEstandar, usePesajesDeLote } from '@/lib/queries/pesajes';
import { useParteDelDiaPorCamada, useRegistrosDeLote } from '@/lib/queries/registrosDiarios';
import { useSalidasCamada } from '@/lib/queries/salidas';
import type { RegistroDiario } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';

const tabs = ['Resumen', 'Sensores', 'Registros', 'Pesos'] as const;
type Tab = (typeof tabs)[number];

export function DetalleNavePage() {
  const role = useAuthStore((s) => s.user?.rol);
  const canManage = role === 'admin' || role === 'ganadero';
  const { naveId: naveParam } = useParams<{ naveId: string }>();
  const naveId = useEntityId(naveParam, 'naves');
  const [params, setParams] = useSearchParams();
  const requestedCamadaId = useEntityId(params.get('camada'), 'camadas');
  const activeQ = useCamadaActiva();
  const requestedQ = useCamada(requestedCamadaId);
  const camadaQ = requestedCamadaId == null ? activeQ : requestedQ;
  const camada = camadaQ.data;
  const navesQ = useNaves();
  const lotesQ = useLotesDeCamada(camada?.id ?? null);
  const nave = navesQ.data?.find((n) => n.id === naveId);
  const lote = lotesQ.data?.find((l) => l.nave_id === naveId);
  const pesajesQ = usePesajesDeLote(lote?.id ?? null);
  const curvaQ = useCurvaEstandar(lote?.id ?? null);
  const registrosQ = useRegistrosDeLote(lote?.id ?? null);
  const salidasQ = useSalidasCamada(camada?.id ?? null);
  const sensorsQ = useSensoresNave(naveId);
  const bundle = useNaveHistoryBundle();
  const today = format(new Date(), 'yyyy-MM-dd');
  const cierre = camada?.fecha_cierre?.slice(0, 10);
  const hasta = camada?.cerrada && cierre && cierre < today ? cierre : today;
  const parteQ = useParteDelDiaPorCamada(camada?.id ?? null, hasta);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDevice, selectDevice] = useState<number | string | null>(null);
  const [historyOpen, openHistory] = useState(false);
  const tab = tabs.find((t) => t.toLowerCase() === params.get('tab')) ?? 'Resumen';
  const changeTab = (value: Tab) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', value.toLowerCase());
        return next;
      },
      { replace: true },
    );
  const sensorSelect = (deviceId: number | string | null) => {
    selectDevice(deviceId);
    changeTab('Sensores');
  };

  const registros = registrosQ.data?.filter((r) => r.fecha <= hasta);
  const pesajes = pesajesQ.data?.filter((p) => p.fecha <= hasta);
  const bajas = registros?.reduce((sum, r) => sum + r.bajas_muerte + r.bajas_triaje, 0);
  const salidas = salidasQ.data
    ?.filter((s) => !s.anulada && s.fecha_carga <= hasta)
    .flatMap((s) => s.lineas)
    .filter((l) => l.lote_nave_id === lote?.id)
    .reduce((sum, l) => sum + l.unidades_cargadas, 0);
  const alive =
    lote && bajas != null && salidas != null
      ? Math.max(0, lote.num_animales_asignados - bajas - salidas)
      : null;
  const mortality =
    lote && lote.num_animales_asignados > 0 && bajas != null
      ? (bajas / lote.num_animales_asignados) * 100
      : null;
  const curva = curvaQ.data;
  const sample =
    camada && pesajes
      ? sampleSummary(pesajes, hasta, camada.fecha_entrada, curva?.puntos ?? [])
      : null;
  const equipment = getSensorEquipment(nave, sensorsQ.data);
  const deniedSensors = [401, 403].includes(
    (sensorsQ.error as { status?: number } | null)?.status ?? 0,
  );
  const sensorData =
    !deniedSensors && (nave?.sensor_equipment != null || sensorsQ.data != null)
      ? applySensorEquipment(sensorsQ.data, equipment, naveId ?? undefined)
      : undefined;
  const devices = sensorData?.environment ?? [];
  const configureSensors =
    canManage && naveId != null ? `/ajustes/naves/${naveId}/sensores` : undefined;
  const temperature = sensorAverage(devices.map((d) => d.temperature));
  const humidity = sensorAverage(devices.map((d) => d.humidity));
  const received = devices
    .flatMap((d) => [d.temperature?.received_at, d.humidity?.received_at])
    .filter((d): d is string => !!d)
    .sort();
  const day = camada
    ? Math.max(1, differenceInCalendarDays(parseISO(hasta), parseISO(camada.fecha_entrada)) + 1)
    : null;
  const days = camada
    ? Array.from(
        {
          length: Math.max(
            0,
            differenceInCalendarDays(parseISO(hasta), parseISO(camada.fecha_entrada)) + 1,
          ),
        },
        (_, age) => ({
          age,
          fecha: format(addDays(parseISO(camada.fecha_entrada), age), 'yyyy-MM-dd'),
        }),
      )
    : [];
  const missing =
    registros && days.filter((d) => !registros.some((r) => r.fecha === d.fecha)).length;
  const history =
    camada && naveId != null && bundle ? historicalLosses(bundle, camada, naveId) : null;
  const lossPoints = days.map((d) => ({
    ...d,
    actual: registros
      ? registros.find((r) => r.fecha === d.fecha)
        ? registros
            .filter((r) => r.fecha === d.fecha)
            .reduce((sum, r) => sum + r.bajas_muerte + r.bajas_triaje, 0)
        : null
      : null,
    reference: history?.byAge.get(d.age)?.value ?? null,
    count: history?.byAge.get(d.age)?.count ?? 0,
  }));
  const recent = lossPoints.slice(-7);
  const comparable =
    recent.length === 7 && recent.every((p) => p.actual != null && p.reference != null);
  const referenceTotal = recent.reduce((sum, p) => sum + (p.reference ?? 0), 0);
  const delta =
    comparable && referenceTotal > 0
      ? (recent.reduce((sum, p) => sum + p.actual!, 0) / referenceTotal - 1) * 100
      : null;
  const weightPoints = days.map((d) => {
    const value =
      camada && pesajes
        ? sampleSummary(pesajes, d.fecha, camada.fecha_entrada, curva?.puntos ?? [])
        : null;
    const expected = expectedWeight(d.age, curva?.puntos ?? []);
    return { ...d, actual: value?.fecha === d.fecha ? value.peso : null, reference: expected };
  });
  const currentRecord = registros?.find((r) => r.fecha === hasta);
  const part = parteQ.data?.registros.find((r) => r.lote_nave_id === lote?.id);
  const registered = part?.id != null || currentRecord != null;
  const openPart = (fecha: string) =>
    navigate(`/camadas/${camada?.id}/registro-diario?fecha=${fecha}&lote=${lote?.id}`, {
      state: { backgroundLocation: location },
    });
  const edit = canManage && !camada?.cerrada && !!lote;
  const previousParts = [...(registros ?? [])]
    .filter((r) => r.fecha < hasta)
    .sort((a, b) => b.fecha.localeCompare(a.fecha));
  const currentNaveIndex = navesQ.data?.findIndex((n) => n.id === naveId) ?? -1;
  const switchNave = (direction: number) => {
    const next = navesQ.data?.[currentNaveIndex + direction];
    if (next) {
      selectDevice(null);
      navigate(`/naves/${next.id}${params.size ? `?${params}` : ''}`);
    }
  };
  const recordRows = (records: RegistroDiario[]) => (
    <ul className="nave-history-list">
      {records.map((r) => (
        <li key={r.id}>
          {!camada?.cerrada && !!lote ? (
            <button onClick={() => openPart(r.fecha)}>
              <span>{fmtFecha(r.fecha)}</span>
              <span>
                {fmtInt(r.bajas_muerte)} <small>muertes</small>
              </span>
              <span>
                {fmtInt(r.bajas_triaje)} <small>triajes</small>
              </span>
              <ChevronRight size={15} aria-hidden />
            </button>
          ) : (
            <div>
              <span>{fmtFecha(r.fecha)}</span>
              <span>{fmtInt(r.bajas_muerte)} muertes</span>
              <span>{fmtInt(r.bajas_triaje)} triajes</span>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
  const partAction = (
    <button
      className="nave-part-action"
      disabled={!lote || camada?.cerrada}
      onClick={() => openPart(hasta)}
    >
      <ClipboardCheck size={21} aria-hidden />
      <span>
        <strong>
          {!canManage
            ? 'Consultar parte de hoy'
            : registered
              ? 'Parte de hoy registrado'
              : 'Registrar parte de hoy'}
        </strong>
        <small>
          {parteQ.data?.local_pending
            ? 'En el móvil · Pendiente de sincronizar'
            : registered
              ? `${fmtInt(currentRecord?.bajas_muerte ?? part?.bajas_muerte ?? 0)} muertes · ${fmtInt(currentRecord?.bajas_triaje ?? part?.bajas_triaje ?? 0)} triajes`
              : parteQ.data || registros
                ? 'Pendiente en esta nave'
                : 'Parte sin actualizar'}
        </small>
      </span>
      <ChevronRight size={17} aria-hidden />
    </button>
  );

  return (
    <main className="nave-page mx-auto max-w-md px-4">
      <header className="ui-global-header mb-3" data-testid="app-header">
        <AppHeaderContent
          backTo={requestedCamadaId ? `/camadas?camada=${requestedCamadaId}` : '/'}
          backLabel={requestedCamadaId ? 'Camadas' : 'Inicio'}
        />
      </header>
      {navesQ.isLoading || camadaQ.isLoading || lotesQ.isLoading ? (
        <p role="status">Cargando nave…</p>
      ) : camadaQ.isError && !camada ? (
        <p role="alert">Camada no disponible. Conecta para actualizar.</p>
      ) : requestedCamadaId != null && !camada ? (
        <p role="alert">Camada no encontrada.</p>
      ) : !nave ? (
        <p role="alert">
          {navesQ.isError
            ? 'Naves no disponibles. Conecta para actualizar.'
            : 'Nave no encontrada.'}
        </p>
      ) : (
        <>
          <div className="nave-title-row">
            <h1 className="ui-page-title">{nave.nombre || nave.codigo}</h1>
            <div className="nave-pager" aria-label="Cambiar de nave">
              <button
                className="ui-icon-button"
                aria-label="Nave anterior"
                disabled={currentNaveIndex <= 0}
                onClick={() => switchNave(-1)}
              >
                <ChevronLeft size={20} aria-hidden />
              </button>
              <span>
                {currentNaveIndex + 1}/{navesQ.data?.length}
              </span>
              <button
                className="ui-icon-button"
                aria-label="Nave siguiente"
                disabled={currentNaveIndex >= (navesQ.data?.length ?? 0) - 1}
                onClick={() => switchNave(1)}
              >
                <ChevronRight size={20} aria-hidden />
              </button>
            </div>
          </div>
          {camada && (
            <p className="nave-context">
              <span>{camada.codigo_lote}</span>
              <span>
                Día {day} · {camada.sexo === 'machos' ? 'Machos' : 'Hembras'}
                {camada.cerrada ? ' · Finalizada' : ''}
              </span>
            </p>
          )}
          {lotesQ.isError && !lotesQ.data ? (
            <p role="alert">Asignaciones no disponibles. Conecta para actualizar.</p>
          ) : !lote ? (
            <>
              <p>Esta nave no tiene un lote asignado en la camada seleccionada.</p>
              {canManage && !camada?.cerrada && (
                <Link
                  className="ui-primary w-full"
                  to={camada ? `/camadas/${camada.id}/asignar-lotes` : '/camadas/nueva'}
                >
                  Asignar animales
                </Link>
              )}
              <NaveSensors
                data={sensorData}
                loading={sensorsQ.isLoading}
                error={sensorsQ.isError}
                selectedDevice={selectedDevice}
                onSelect={selectDevice}
                configureTo={configureSensors}
              />
            </>
          ) : (
            <>
              <section className="nave-identity" aria-label="Indicadores de la nave">
                <div className="nave-core">
                  {[
                    {
                      Icon: Bird,
                      name: 'Aves vivas',
                      value: alive == null ? '—' : fmtInt(alive),
                      target: 'Registros' as Tab,
                      test: 'nave-kpi-vivas',
                    },
                    {
                      Icon: HeartPulse,
                      name: 'Mortalidad',
                      value: mortality == null ? '—' : fmt1(mortality),
                      unit: '%',
                      target: 'Registros' as Tab,
                      test: 'nave-kpi-mortalidad',
                    },
                    {
                      Icon: Weight,
                      name: 'Último peso',
                      value: sample ? formatoDecimal(sample.peso, 2) : '—',
                      unit: 'kg',
                      target: 'Pesos' as Tab,
                      test: 'nave-kpi-peso',
                    },
                  ].map(({ Icon, name, value, unit, target, test }) => (
                    <button
                      key={name}
                      aria-label={`${name}: ${value}${unit ?? ''}. Consultar ${target.toLowerCase()}`}
                      onClick={() => changeTab(target)}
                    >
                      <span className="nave-core-icon">
                        <Icon size={18} aria-hidden />
                      </span>
                      <strong data-testid={test}>
                        {value}
                        {unit && value !== '—' && (
                          <>
                            {' '}
                            <small>{unit}</small>
                          </>
                        )}
                      </strong>
                      <span>{name}</span>
                    </button>
                  ))}
                </div>
                <p className="nave-identity-caption">
                  {bajas == null || salidas == null
                    ? 'Saldo sin actualizar'
                    : `Saldo registrado${missing ? ' · Partes incompletos' : ''}`}{' '}
                  ·{' '}
                  {pesajes == null
                    ? 'Peso sin actualizar'
                    : sample
                      ? `Peso: ${fmtFecha(sample.fecha)}`
                      : 'Sin muestreo'}
                </p>
                {!!devices.length && (
                  <>
                    <div className="nave-climate">
                      {[
                        {
                          Icon: Thermometer,
                          name: 'Temperatura media',
                          provenance: simulationLabel(
                            devices.map((d) => d.temperature),
                            temperature.value != null,
                          ),
                          value: temperature,
                          unit: '°C',
                          test: 'nave-kpi-temperatura',
                        },
                        {
                          Icon: Droplet,
                          name: 'Humedad media',
                          provenance: simulationLabel(
                            devices.map((d) => d.humidity),
                            humidity.value != null,
                          ),
                          value: humidity,
                          unit: '%',
                          test: 'nave-kpi-humedad',
                        },
                      ].map(({ Icon, name, value, unit, test, provenance }) => (
                        <button
                          key={name}
                          onClick={() => changeTab('Sensores')}
                          aria-label={`${name}. Consultar sensores`}
                        >
                          <Icon size={18} aria-hidden />
                          <span>
                            <small>{name}</small>
                            {provenance && <small>{provenance}</small>}
                            <strong data-testid={test}>
                              {value.value == null ? '—' : fmt1(value.value)} <small>{unit}</small>
                            </strong>
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="nave-identity-caption">
                      {sensorData
                        ? `Temperatura: ${temperature.available}/${temperature.total} · Humedad: ${humidity.available}/${humidity.total} estaciones`
                        : 'Sensores sin actualizar'}
                      {sensorsQ.data?.local_snapshot
                        ? ' · Lecturas conservadas'
                        : received.at(-1)
                          ? ` · ${fmtFechaHora(received.at(-1)!)}`
                          : ''}
                    </p>
                  </>
                )}
                {!sensorData && (
                  <p className="nave-identity-caption">
                    {sensorsQ.isError ? 'Sensores sin actualizar' : 'Consultando sensores…'}
                  </p>
                )}
              </section>
              <div className="nave-tabs" role="tablist" aria-label="Secciones de la nave">
                {tabs.map((name, index) => (
                  <button
                    key={name}
                    role="tab"
                    id={`nave-tab-${name}`}
                    aria-controls="nave-panel"
                    aria-selected={tab === name}
                    tabIndex={tab === name ? 0 : -1}
                    onClick={() => changeTab(name)}
                    onKeyDown={(e) => {
                      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
                      e.preventDefault();
                      const next =
                        e.key === 'Home'
                          ? 0
                          : e.key === 'End'
                            ? 3
                            : (index + (e.key === 'ArrowRight' ? 1 : 3)) % 4;
                      changeTab(tabs[next]);
                      document.getElementById(`nave-tab-${tabs[next]}`)?.focus();
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <section
                role="tabpanel"
                id="nave-panel"
                aria-labelledby={`nave-tab-${tab}`}
                className="nave-tab-content"
              >
                {tab === 'Resumen' && (
                  <>
                    {!camada?.cerrada && partAction}
                    <NaveSensorAlerts data={sensorData} onSelect={sensorSelect} />
                    <button className="nave-loss-conclusion" onClick={() => changeTab('Registros')}>
                      <span>
                        <strong>
                          {delta == null
                            ? comparable && referenceTotal === 0
                              ? recent.some((p) => p.actual! > 0)
                                ? 'Bajas registradas frente a una media de cero'
                                : 'Sin bajas frente a una media de cero'
                              : 'Comparación incompleta'
                            : Math.abs(delta) < 1
                              ? 'Similar a la media histórica'
                              : `${fmtInt(Math.abs(delta))} % ${delta > 0 ? 'más' : 'menos'} bajas que la media`}
                        </strong>
                        <small>Últimos 7 días · Misma nave, sexo, fase y edad</small>
                        <small>
                          {history
                            ? `${history.camadas} camadas con registros`
                            : 'Histórico no disponible. Prepara la copia offline.'}
                        </small>
                      </span>
                      <ChevronRight size={17} aria-hidden />
                    </button>
                    {missing != null && missing > 0 && (
                      <p className="nave-caption">
                        {missing} días sin parte. El saldo usa las bajas registradas.
                      </p>
                    )}
                  </>
                )}
                {tab === 'Sensores' && (
                  <NaveSensors
                    data={sensorData}
                    loading={sensorsQ.isLoading}
                    error={sensorsQ.isError}
                    selectedDevice={selectedDevice}
                    onSelect={selectDevice}
                    configureTo={configureSensors}
                  />
                )}
                {tab === 'Registros' && (
                  <>
                    {!camada?.cerrada && partAction}
                    <NaveEvolutionChart
                      key={`loss-${lote.id}`}
                      points={lossPoints}
                      title="Evolución de bajas"
                      unit="bajas"
                      referenceLabel="Media histórica"
                    />
                    <p className="nave-caption">
                      Muertes y triajes por día · Misma nave, sexo, fase y edad.{' '}
                      {history
                        ? `${history.camadas} camadas con registros.`
                        : 'Histórico no disponible; prepara la copia offline.'}{' '}
                      Los días sin parte son huecos.
                      {bundle?.prepared_at ? ` Copia: ${fmtFechaHora(bundle.prepared_at)}.` : ''}
                    </p>
                    <section aria-label="Partes anteriores">
                      <h2>Partes anteriores</h2>
                      {registrosQ.isLoading ? (
                        <p>Cargando partes…</p>
                      ) : registrosQ.data ? (
                        previousParts.length ? (
                          recordRows(previousParts.slice(0, 3))
                        ) : (
                          <p className="nave-caption">Todavía no hay partes anteriores.</p>
                        )
                      ) : (
                        <p role="alert">Partes no disponibles. Conecta para actualizar.</p>
                      )}
                      <button
                        className="nave-history-link"
                        disabled={!registros}
                        onClick={() => openHistory(true)}
                      >
                        Ver todos los partes <ChevronRight size={17} aria-hidden />
                      </button>
                    </section>
                    <details className="nave-disclosure">
                      <summary>Origen del saldo</summary>
                      {[
                        ['Animales asignados', lote.num_animales_asignados],
                        ['Salidas', salidas],
                        ['Bajas acumuladas', bajas],
                      ].map(([name, value]) => (
                        <div className="nave-reading" key={String(name)}>
                          <span>{name}</span>
                          <strong>{value == null ? '—' : fmtInt(Number(value))}</strong>
                        </div>
                      ))}
                      <p className="nave-caption">
                        Mortalidad: muertes y triajes sobre los animales asignados a esta nave.
                      </p>
                    </details>
                  </>
                )}
                {tab === 'Pesos' && (
                  <>
                    {edit && (
                      <button
                        className="ui-primary w-full"
                        onClick={() =>
                          navigate(
                            `/naves/${nave.id}/pesajes/nuevo?camada=${camada!.id}&lote=${lote.id}`,
                            {
                              state: { backgroundLocation: location },
                            },
                          )
                        }
                      >
                        <Weight size={19} aria-hidden />
                        <span>Registrar muestreo</span>
                        <ChevronRight size={17} aria-hidden />
                      </button>
                    )}
                    <NaveEvolutionChart
                      key={`weight-${lote.id}`}
                      points={weightPoints}
                      title="Evolución del peso"
                      initialDate={sample?.fecha}
                      unit="kg"
                      referenceLabel={
                        curva?.origen === 'historico' ? 'Curva histórica' : 'Curva esperada'
                      }
                      weight
                    />
                    <p className="nave-caption">
                      {curva?.origen === 'historico'
                        ? `${curva.camadas_historico} camadas del mismo sexo, todas las naves.`
                        : curva?.origen === 'ajustes'
                          ? 'Curva configurada en Ajustes.'
                          : 'Sin curva de referencia disponible.'}{' '}
                      Edad desde la entrada: día 0. La línea une los muestreos registrados.
                    </p>
                    <section aria-label="Muestreos">
                      <h2>Muestreos</h2>
                      {pesajesQ.isLoading ? (
                        <p>Cargando muestreos…</p>
                      ) : pesajes ? (
                        pesajes.length ? (
                          <>
                            <ul className="nave-samples">
                              {[...pesajes]
                                .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id)
                                .slice(0, 3)
                                .map((p) => (
                                  <li key={p.id}>
                                    <span>
                                      {fmtFecha(p.fecha)}
                                      <small>{fmtInt(p.num_pavos)} aves muestreadas</small>
                                    </span>
                                    <strong>{formatoDecimal(p.peso_medio_kg, 2)} kg</strong>
                                  </li>
                                ))}
                            </ul>
                            {pesajes.length > 0 && (
                              <Link
                                className="nave-history-link"
                                data-testid="nave-ver-todos-muestreos"
                                to={`/naves/${nave.id}/muestreos${requestedCamadaId != null ? `?camada=${camada!.id}` : ''}`}
                              >
                                Ver todos los muestreos ({pesajes.length}){' '}
                                <ChevronRight size={17} aria-hidden />
                              </Link>
                            )}
                          </>
                        ) : (
                          <p className="nave-caption">Sin muestreos registrados.</p>
                        )
                      ) : (
                        <p role="alert">Muestreos no disponibles. Conecta para actualizar.</p>
                      )}
                    </section>
                  </>
                )}
              </section>
            </>
          )}
        </>
      )}
      {historyOpen && (
        <ModalOverlay
          placement="bottom"
          className="max-w-md"
          ariaLabelledby="nave-history-title"
          onBackdrop={() => openHistory(false)}
        >
          <header className="nave-title-row">
            <h2 id="nave-history-title">Todos los partes</h2>
            <button
              className="ui-icon-button"
              aria-label="Cerrar historial"
              onClick={() => openHistory(false)}
            >
              <X size={22} aria-hidden />
            </button>
          </header>
          <p className="nave-caption">
            {nave?.nombre || nave?.codigo} · {camada?.codigo_lote}
          </p>
          {registros?.length ? (
            recordRows([...registros].sort((a, b) => b.fecha.localeCompare(a.fecha)))
          ) : (
            <p>No hay partes registrados.</p>
          )}
        </ModalOverlay>
      )}
    </main>
  );
}
